<script>
import app from 'app';
import formatter from '../formatter';

export default {
  props: {
    colorData: {
      type: Object,
      required: false,
    },

    defaultSolid: {
      type: String,
      default: '#B87DFF',
    },

    useFormatter: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      curData: null,
      curColorHex: null,
      curType: null,
      curHsl: null,
    };
  },

  beforeCreate() {
    this.$options.colorTypes = {
      SOLID: 'Solid',
      GRADIENT: 'Gradient',
    };

    this.$options.gradientTypes = {
      LINEAR: 'LINEAR',
      RADIAL: 'RADIAL',
    };
  },

  watch: {
    colorData: {
      handler(newData) {
        // if is null, don't update color!
        if (_.isNull(newData)) {
          return;
        }
        // if empty, set default solid
        if (_.isEmpty(newData)) {
          newData = this.createSolid(this.defaultSolid);
        }
        this.updateCurData(this.parseColor(newData));
        this.setTypeByData();
        // is solid
        if (this.isSolid) {
          this.setCurColor(this.curData.colorValue);
        } else if (this.isGradient) {
          // is gradient
          this.updateSelectedGradient('start');
          this.setCurColor(this.curData.start.color);
        }
        this.clearHsl();
      },
      immediate: true,
      deep: true,
    },

    curHsl: {
      handler(newHsl) {
        if (_.isEmpty(newHsl)) {
          this.setHsl(this.curColorHex);
        } else {
          this.setCurColor(this.hslToHex(newHsl));
        }
      },
      immediate: true,
      deep: true,
    },

    curData: {
      handler(newData) {
        this.$emit(
          'input',
          this.useFormatter ? this.formatColor(newData) : newData
        );
      },
      deep: true,
    },
  },

  computed: {
    backgroundReflection() {
      // computeBgFromColorData works with server format
      return app.computeBgFromColorData(this.formatColor(this.curData));
    },

    backgroundGradientReflection() {
      const curData = this.curData;
      const startColor =
        (curData.start && curData.start.color) || this.defaultSolid;
      const endColor = (curData.end && curData.end.color) || this.defaultSolid;

      return app.computeBgFromColorData(
        this.formatColor(this.createGradient(startColor, endColor))
      );
    },

    gradientAngles() {
      return {
        horizontal: {
          text: 'Horizontal',
          icon: 'arrows-horizontal',
          value: 90,
        },
        vertical: {
          text: 'Vertical',
          icon: 'arrows-vertical',
          value: 0,
        },
        radial: {
          text: 'Radial',
          icon: 'Radial',
          value: 100,
        },
      };
    },

    toRgb() {
      return formatter.hexToRgb(this.curColorHex);
    },

    toHsl() {
      return this.curHsl;
    },

    displayTypes() {
      return [
        this.$options.colorTypes.SOLID,
        this.$options.colorTypes.GRADIENT,
      ];
    },

    displayCurType() {
      return this.curType;
    },

    isGradient() {
      return this.curType === this.$options.colorTypes.GRADIENT;
    },

    isSolid() {
      return this.curType === this.$options.colorTypes.SOLID;
    },
  },

  methods: {
    rgbToHex({ r, g, b }) {
      const curRgb = this.toRgb;
      return formatter.rgbToHex(
        _.isDefined(r) ? r : curRgb.r,
        _.isDefined(g) ? g : curRgb.g,
        _.isDefined(b) ? b : curRgb.b
      );
    },

    hslToHex({ h, s, l }) {
      const curHsl = this.toHsl;
      return formatter.hslToHex(
        _.isDefined(h) ? h : curHsl.h,
        _.isDefined(s) ? s : curHsl.s,
        _.isDefined(l) ? l : curHsl.l
      );
    },

    updateHsl(partialUpdate) {
      this.curHsl = Object.assign(this.curHsl, partialUpdate);
    },

    setHsl(hex) {
      this.curHsl = formatter.hexToHsl(hex);
    },

    clearHsl() {
      this.curHsl = {};
    },

    updateHex(hex) {
      this.setCurColor(hex);
      this.setHsl(hex);
    },

    updateCurData(newData) {
      // create a shallow copy of the original object
      this.curData = Object.assign({}, newData);
    },

    setCurColor(hex) {
      // if update exist, not first time!
      if (this.curColorHex) {
        // solid
        if (this.isSolid) {
          this.updateSolidColor(hex);
        } else {
          // gradient
          if (this.curData._selectedGradient === 'start') {
            this.updateStartColor(hex);
          } else if (this.curData._selectedGradient === 'end') {
            this.updateEndColor(hex);
          } else {
            console.error(
              'setCurColor: _selectedGradient should be start or end!'
            );
          }
        }
      }

      this.curColorHex = hex;
    },

    updateGradientColor(hex, handleName) {
      this.updateSelectedGradient(handleName);
      this.setCurColor(hex);
      this.clearHsl();
    },

    updateGradientType(newGradientType) {
      this.curData.gradientType = newGradientType;
    },

    updateAngle(newAngle) {
      // update gradient type as well
      this.updateGradientType(
        newAngle === this.gradientAngles.radial.value
          ? this.$options.gradientTypes.RADIAL
          : this.$options.gradientTypes.LINEAR
      );

      this.curData.angle = newAngle;
    },

    updateStartColor(newColor) {
      this.curData.start.color = newColor;
    },

    updateStartPosition(newPosition) {
      this.curData.start.position = newPosition;
    },

    updateEndColor(newColor) {
      this.curData.end.color = newColor;
    },

    updateEndPosition(newPosition) {
      this.curData.end.position = newPosition;
    },

    updateSolidColor(newColor) {
      this.curData.colorValue = newColor;
    },

    updateSelectedGradient(selectedName) {
      this.curData._selectedGradient = selectedName;
    },

    setTypeByData() {
      // solid
      if (this.curData.colorValue) {
        this.changeType(this.$options.colorTypes.SOLID);
      } else if (this.curData.gradientType) {
        // gradient
        this.changeType(this.$options.colorTypes.GRADIENT);
        // error
      } else {
        console.error('color-picker: setTypeByData: no type!');
      }
    },

    changeType(newType) {
      this.curType = newType;
    },

    changeTypeManually(newType) {
      // first, update new type
      this.changeType(newType);

      // solid
      if (this.isSolid) {
        this.updateCurData(this.createSolid(this.curColorHex));
      } else if (this.isGradient) {
        // gradient
        this.updateCurData(this.createGradient(this.curColorHex));
        // error
      } else {
        console.error('color-picker: changeTypeManually: no type!');
      }

      this.setHsl(this.curColorHex);
    },

    swapColors() {
      const oldStartColor = this.curData.start.color;
      const oldStartPosition = this.curData.start.position;
      const oldEndColor = this.curData.end.color;
      const oldEndPosition = this.curData.end.position;

      this.updateStartColor(oldEndColor);
      this.updateStartPosition(oldEndPosition);
      this.updateEndColor(oldStartColor);
      this.updateEndPosition(oldStartPosition);
      this.updateSelectedGradient(
        this.curData._selectedGradient === 'start' ? 'end' : 'start'
      );
    },

    createSolid(colorValue) {
      return {
        colorValue: colorValue,
      };
    },

    createGradient(startColor, endColor) {
      return {
        gradientType: this.$options.gradientTypes.LINEAR,
        angle: 90,
        start: {
          color: startColor,
          position: 0,
        },
        end: {
          color: endColor || formatter.darkenColor(startColor, 20),
          position: 100,
        },
        _selectedGradient: 'start',
      };
    },

    isValidHex(hex) {
      return formatter.isValidHex(hex);
    },

    // parser from server format to color picker format
    // - if has gradientColors, modify it to work with color picker
    parseColor(colorData) {
      const pColorData = _.omit(colorData, 'gradientColors');
      const gradientColors = colorData.gradientColors;

      if (_.isArray(gradientColors) && gradientColors.length > 1) {
        const start = gradientColors[0];
        const end = gradientColors[1];

        pColorData.start = {
          color: formatter.toHex(start.color),
          position: start.position,
        };

        pColorData.end = {
          color: formatter.toHex(end.color),
          position: end.position,
        };
      }

      return pColorData;
    },

    // format from color picker format to server format
    // - can be disabled with prop: useFormatter=false
    formatColor(colorData) {
      const fColorData = _.omit(colorData, 'start', 'end', '_selectedGradient');

      if (_.isPlainObject(colorData.start) && _.isPlainObject(colorData.end)) {
        const start = colorData.start;
        const end = colorData.end;

        fColorData.gradientColors = [
          { color: start.color, position: start.position },
          { color: end.color, position: end.position },
        ];
      }

      return fColorData;
    },
  },
};
</script>
